import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import { ProLayout } from '@ant-design/pro-components'
import logoWithName from '@app/icare/assets/logo-green.svg?inline'
import LogoIcon from '@app/icare/assets/logo-icon.svg?react'
import { useAuth } from '@app/icare/client/auth'
import { signOut } from '@app/icare/client/auth/auth'
import { db } from '@app/icare/client/db'
import { sync } from '@app/icare/client/sync'
import { SyncStatusIndicator } from '@app/icare/components/sync/SyncStatusIndicator'
import { entityRegistry } from '@app/icare/entities/registry'
import { formatUserName } from '@app/icare/entities/user/schema/User'
import { tourSearchSchema } from '@app/icare/module/tour/types'
import { useRouteTour } from '@app/icare/module/tour/useRouteTour'
import { AppAbilityProvider } from '@app/icare/shared/auth/AppAbilityProvider'
import type { AppRoute } from '@app/icare/shared/auth/ability'
import { useSidebarExpanded } from '@app/icare/state/app'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { SyncProvider } from '@pkg/sync/client'
import { useAntdProLayout, useStaticDataMerge } from '@pkg/ui/hooks'
import type { RuntimePageContainerProps } from '@pkg/ui/layout/PageContainer'
import { PageContainer } from '@pkg/ui/layout/PageContainer'
import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { Avatar, Button, Divider, Dropdown, Grid, Spin, Tour } from 'antd'
import { type ReactNode, useRef } from 'react'
import { FaSignOutAlt } from 'react-icons/fa'
import { FaChartLine, FaHouse, FaLocationDot, FaUser } from 'react-icons/fa6'
import { twMerge } from 'tailwind-merge'

export const Route = createFileRoute('/_app')({
  component: RouteComponent,
  validateSearch: zodValidator(tourSearchSchema),
  loader: () => ({
    breadcrumb: {
      label: <FaHouse />,
      to: '/',
    },
  }),
})

const proLayoutToken = {
  bgLayout: 'var(--color-gray-100)',
  sider: {
    paddingInlineLayoutMenu: 0,
    colorMenuBackground: 'var(--color-gray-100)',
    colorBgMenuItemSelected: 'var(--color-primary-100)',
    colorTextMenuSelected: 'var(--color-primary-500)',
    colorTextSubMenuSelected: 'var(--color-primary-500)',
  },
  header: {
    colorBgHeader: 'white',
    colorBgScrollHeader: 'white',
  },
  pageContainer: {
    paddingInlinePageContainerContent: 16,
    paddingBlockPageContainerContent: 16,
  },
}

const addressEntities = [
  entityRegistry.pharmacy,
  entityRegistry.costCarrier,
  entityRegistry.clinic,
  entityRegistry.medicalTechnology,
  entityRegistry.careService,
  entityRegistry.nursingHome,
  entityRegistry.doctor,
  entityRegistry.medicalSupplyHome,
  entityRegistry.hospice,
]

const confidoEntities = [entityRegistry.adTeam, entityRegistry.article, entityRegistry.user]

const fallbackRender = (progress: number | null) => (
  <div className="flex flex-col items-center justify-center h-screen gap-y-4">
    <Spin size="large" />
    <h6 data-testid="boot-progress">
      Warten
      {progress !== null ? ` ${progress.toFixed(1)}%` : ''}
    </h6>
  </div>
)

const errorRender = (err: Error) => (
  <div className="flex flex-col items-center justify-center h-screen gap-y-4">
    <h6>Fehler beim Laden der Datenbank</h6>
    <p>{err.message}</p>
  </div>
)

const menuItemRender = (item: { path?: string; params?: never }, dom: ReactNode) =>
  item.path ? (
    <Link to={item.path} params={item.params} preload="intent" className="flex grow items-center w-100 justify-between">
      {dom}
    </Link>
  ) : (
    dom
  )

function RouteComponent() {
  const { session, allowed } = useAuth()
  const navigate = useNavigate()

  const sidebarRef = useRef<HTMLDivElement>(null)
  const syncRef = useRef<HTMLDivElement>(null)

  const shellIntroTour = useRouteTour('app-shell-intro', [
    {
      title: t`Hauptnavigation`,
      description: t`Dies ist die Hauptnavigation. Über die Leiste auf der linken Seite gelangst du in alle Bereiche von iCare.`,
      target: sidebarRef.current,
      placement: 'right',
    },
    {
      title: t`Sync-Status`,
      description: t`Hier siehst du den aktuellen Sync-Status. Grün bedeutet aktuell, Gelb älter als 5 Minuten, Rot älter als 1 Tag.`,
      target: syncRef.current,
      placement: 'right',
    },
  ])

  const { data: layout, refKey: layoutKey } = useStaticDataMerge(data => data.layout, {})
  const { data: pageContainer } = useStaticDataMerge<RuntimePageContainerProps>(data => data.pageContainer, {})
  const [sidebarExpanded, setSidebarExpanded, toggleSidebar] = useSidebarExpanded()

  const screens = Grid.useBreakpoint()
  const isDesktop = screens.md

  const appNavigationRoutes = [
    {
      key: 'dashboards',
      name: t`Dashboards`,
      icon: <FaChartLine />,
      children: (
        [
          { path: '/notifications', name: t`Meldungen` },
          { path: '/orders', name: t`Bedarfsermittlungen` },
          { path: '/progress-reports', name: t`Verläufe` },
          { path: '/bia-measurements', name: t`BIA-Messungen` },
          { path: '/signed', name: t`Unterschriften` },
          { path: '/orders-stock', name: t`Lager` },
          { path: '/master-data-sheets', name: t`Stammblätter` },
          { path: '/my-patients', name: t`Meine Patient:innen` },
          { path: '/registered-patients', name: t`Neue Patient:innen` },
          { path: '/missing-data', name: t`Fehlende Daten` },
        ] satisfies { path: AppRoute; name: string }[]
      ).filter(c => allowed('read', c.path)),
    },
    {
      path: '/patienten',
      name: t`Patient:innen`,
      icon: <entityRegistry.patient.icon />,
    },
    {
      path: 'entity',
      name: <Trans>Adressen</Trans>,
      icon: <FaLocationDot />,
      routes: addressEntities.map(e => ({
        path: e.route?.list,
        icon: <e.icon />,
        name: e.labelPlural,
      })),
    },
    {
      path: 'confido',
      name: <Trans>Confido</Trans>,
      icon: <LogoIcon className="h-4 w-4" />,
      routes: confidoEntities.map(e => ({
        path: e.route?.list,
        icon: <e.icon />,
        name: e.labelPlural,
      })),
    },
  ]

  const proLayout = useAntdProLayout({ collapsed: sidebarExpanded, routes: layout?.route?.routes })

  return (
    <AppAbilityProvider>
      <SyncProvider runtime={sync} fallback={fallbackRender} error={errorRender}>
        <ProLayout
          key={layoutKey}
          fixedHeader={!isDesktop}
          logo={() =>
            sidebarExpanded ? (
              <LogoIcon className="w-4 text-primary-500!" />
            ) : (
              <img src={logoWithName} alt="iCare" className="w-42" />
            )
          }
          title=""
          token={proLayoutToken}
          contentStyle={{
            borderRadius: 12,
            backgroundColor: 'var(--color-gray-50)',
            margin: 8,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.04)',
            minHeight: isDesktop
              ? 'calc(100vh - 16px - var(--devtools-offset, 0px))'
              : 'calc(100vh - 72px - var(--devtools-offset, 0px))',
            maxWidth: 'calc(100% - 16px)',
            boxSizing: 'border-box',
          }}
          hasSiderMenu
          layout="side"
          siderMenuType="sub"
          {...proLayout}
          onMenuHeaderClick={() => void navigate({ to: '/' })}
          route={{
            routes: appNavigationRoutes,
          }}
          collapsed={sidebarExpanded}
          onCollapse={setSidebarExpanded}
          collapsedButtonRender={false}
          avatarProps={{
            icon: (<FaUser />) as ReactNode,
            title: session?.user?.name as string,
            render: (_avatarProps, _dom) => (
              <Dropdown
                placement={'topRight'}
                menu={{
                  items: [
                    {
                      key: 'logout',
                      label: <Trans>Abmelden</Trans>,
                      icon: <FaSignOutAlt />,
                      onClick: () => void signOut(),
                    },
                  ],
                }}
              >
                <div className={twMerge('flex items-center gap-2 cursor-pointer', !sidebarExpanded ? 'pl-4' : '')}>
                  <Avatar size="small" icon={<FaUser />} />
                  {!sidebarExpanded && (
                    <span className="truncate max-w-28 text-sm">{formatUserName(session?.user)}</span>
                  )}
                </div>
              </Dropdown>
            ),
          }}
          actionsRender={() => (
            <div ref={syncRef}>
              <SyncStatusIndicator runtime={sync} db={db} />
            </div>
          )}
          menuItemRender={menuItemRender}
          menuContentRender={(_props: unknown, defaultDom: ReactNode) => <div ref={sidebarRef}>{defaultDom}</div>}
          menuFooterRender={(props: { collapsed?: boolean }) => (
            <>
              <Divider size="small" style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }} />
              <Button
                type="text"
                block
                className={props.collapsed ? '' : 'justify-start! pl-4!'}
                icon={props.collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={toggleSidebar}
              >
                {!props.collapsed && <Trans>Menü einklappen</Trans>}
              </Button>
            </>
          )}
          {...layout}
        >
          <PageContainer titleRoutes={appNavigationRoutes} {...pageContainer}>
            <Outlet />
          </PageContainer>
        </ProLayout>
        <Tour {...shellIntroTour} />
      </SyncProvider>
    </AppAbilityProvider>
  )
}
